import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { createInQuery } from 'actions/createInQuery';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showDeleteNodeDefinitions } from './showDeleteNodeDefinitions';

type NodeDefinitionEntity = Extract<ActionEntity, { type: 'node_definition' }>;

const isAllNodeDefinition = (entities: readonly ActionEntity[]): entities is readonly NodeDefinitionEntity[] =>
  entities.every((entity) => entity.type === 'node_definition');

// Open the flow editor / submit in a new tab (the node editor is a standalone
// app surface, not an in-router route), so these use window.open directly rather
// than the navigate channel.
const openSubmitWindow = (zoneName: string, name: string) => {
  const width = Math.min(1200, window.innerWidth);
  const height = 800;
  const left = window.screenX + window.innerWidth / 2 - width / 2;
  const top = window.screenY + window.innerHeight / 2 - height / 2;
  window.open(
    `/node_submit/${encodeURIComponent(zoneName)}/${encodeURIComponent(name)}`,
    '_blank',
    `noopener,noreferrer,width=${width},height=${height},top=${top},left=${left}`
  );
};

/**
 * Action definitions for customer node definitions. `createNodeDefinition` /
 * `addToTransportNodeDefinition` are scoped; the rest are contextual (single).
 * duplicate/editFlow/submit/create open the node editor in a new tab
 * (window.open); monitorAll uses the navigate channel; delete opens the
 * conflict-aware delete dialog. `duplicate`, `submit` and
 * `addToTransportNodeDefinition` are hidden for deprecated definitions
 * (mirroring the old per-row menu, which offered only edit/monitor/delete).
 */
export const nodeDefinitionActionDefinitions: ActionDefinitions = [
  {
    id: 'createNodeDefinition',
    scope: 'type',
    label: 'New definition',
    resolve: (ctx) => () => window.open(`/node_editor${createInQuery(ctx.createIn)}`, '_blank'),
  },
  {
    id: 'duplicate',
    label: 'Duplicate Definition',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllNodeDefinition(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      if (only.isDeprecated) return 'hidden';
      return () => window.open(`/node_editor?duplicateFromId=${encodeURIComponent(String(only.id))}`, '_blank');
    },
  },
  {
    id: 'editFlow',
    label: 'Edit Node Definition',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllNodeDefinition(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => window.open(`/node_editor/${only.id}/${encodeURIComponent(String(only.name))}`, '_blank');
    },
  },
  {
    id: 'monitorAll',
    label: 'Monitor All Instances',
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllNodeDefinition(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      return () => navigate(`/automation/node_instances?node_definition_id=${only.id}&parentOnly=false`);
    },
  },
  {
    id: 'submit',
    label: 'Submit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllNodeDefinition(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      if (only.isDeprecated) return 'hidden';
      return () => openSubmitWindow(only.zoneName, only.name);
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllNodeDefinition(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      const targets = entities.map((entity) => ({ zoneName: entity.zoneName, name: entity.name }));
      return () => showDeleteNodeDefinitions(targets, refresh);
    },
  },
  {
    id: 'addToTransportNodeDefinition',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllNodeDefinition(entities)) return 'hidden';
      if (entities.some((entity) => entity.isDeprecated)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'node_definition',
        entities.map((entity) => entity.id),
        { objectLabel: single ? `${single.zoneName || '-'} | ${single.name}` : `${entities.length} node definitions` }
      );
    },
  },
];
